import { Badge, Box, IconButton, Loader, Text } from '@wix/design-system';
import { navigateToPath } from './embed-utils';
import {
  RegenerateSparkleSmall,
  DismissFilledSmall,
  FullScreenSmall,
  ExitFullScreenSmall,
} from '@wix/wix-ui-icons-common';
import React from 'react';
import { getBaseURLClient } from '@/utils/baseURL';
import { clog } from '@/utils/clog';
import { ASSISTANT_CONFIG } from '@/utils/constants';
import { useBridgeMessages } from '@/components/ChatWidgetWrapper/useBridgeMessages';

const baseURL = getBaseURLClient();

const TopBar = ({
  onClose,
  toggleFullScreen,
  fullScreen,
  assistantType,
  uiType,
  sendMessageToIframe,
  stream,
}: {
  onClose: () => void;
  toggleFullScreen?: () => void;
  fullScreen?: boolean;
  assistantType: string;
  uiType: string;
  sendMessageToIframe: (type: string, data?: Record<string, any>) => void;
  stream: string;
}) => {
  const assistantConfig =
    ASSISTANT_CONFIG[assistantType as 'SDK' | 'REST' | 'VELO' | 'BUILD_APPS'];

  const isSmallWidth = window.innerWidth < 768;

  return (
    <Box
      direction="vertical"
      verticalAlign="middle"
      minWidth="100%"
      width="100%"
      height="50px"
      minHeight={'50px'}
      padding="0 10px"
      background="#f0f6ff"
      cursor="pointer"
      boxSizing="border-box"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        }}
        onClick={() => {
          onClose();
        }}
      >
        <Box width="100%">
          <Box flexGrow={1} verticalAlign="middle" gap="10px">
            <Text weight="bold" size="small">{`${
              assistantConfig?.niceName || assistantType
            } Assistant`}</Text>
            <Badge skin="standard" size="tiny">
              BETA
            </Badge>
          </Box>
          <Box gap="5px">
            {uiType === 'chat-widget' ? (
              <IconButton
                title="New conversation"
                size="tiny"
                skin="inverted"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  sendMessageToIframe('p2d:digor:clear');
                }}
              >
                <RegenerateSparkleSmall />
              </IconButton>
            ) : null}
            {isSmallWidth ? null : (
              <IconButton
                title={fullScreen ? 'Exit full screen' : 'Full screen'}
                size="tiny"
                skin="inverted"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleFullScreen?.();
                }}
              >
                {fullScreen ? <ExitFullScreenSmall /> : <FullScreenSmall />}
              </IconButton>
            )}
            <IconButton
              title="Close"
              size="tiny"
              skin="inverted"
              onClick={onClose}
            >
              <DismissFilledSmall />
            </IconButton>
          </Box>
        </Box>
      </div>
    </Box>
  );
};

const IframeComponent = React.memo(
  ({
    openedOnce,
    assistantType,
    uiType,
    stream,
    iframeRef,
    iframeURLParams,
  }: {
    openedOnce: boolean;
    assistantType: string;
    uiType: string;
    stream: string;
    iframeRef: React.RefObject<HTMLIFrameElement>;
    iframeURLParams: URLSearchParams;
  }) => {
    const iframeURL = `${baseURL}/${uiType}?${iframeURLParams.toString()}`;

    return (
      <>
        {openedOnce ? (
          <iframe
            id="digor-iframe"
            ref={iframeRef}
            src={iframeURL}
            allow="clipboard-write"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        ) : null}
      </>
    );
  },
  (prev, next) => {
    return (
      prev.openedOnce === next.openedOnce &&
      prev.assistantType === next.assistantType
    );
  }
);

const DIGORIFrame_ = ({
  onClose,
  toggleFullScreen,
  fullScreen,
  open,
  assistantType,
  uiType = 'chat-widget',
  stream = 'false',
  width = '330px',
  height = '500px',
}: {
  onClose: () => void;
  toggleFullScreen?: () => void;
  fullScreen?: boolean;
  open: boolean;
  assistantType: string;
  uiType?: string;
  stream?: string;
  width?: string;
  height?: string;
}) => {
  const assistantConfig =
    ASSISTANT_CONFIG[assistantType as 'SDK' | 'REST' | 'VELO' | 'BUILD_APPS'];

  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = React.useState(false);
  const [openedOnce, setOpenedOnce] = React.useState(false);

  const { sendMessageToIframe } = useBridgeMessages({
    iframe: iframeRef.current,
    onMessageFromIframe: (message) => {
      if (message.type === 'd2p:digor:link-click') {
        clog('DIGORIFrame: should-open-link:', message);
        navigateToPath(message.fullURL, message.path);
      }
      if (message.type === 'd2p:digor:ready') {
        clog('DIGORIFrame: digor-chat-widget is ready');
        setReady(true);
      }
    },
  });

  React.useEffect(() => {
    if (open && !openedOnce) {
      setOpenedOnce(true);
    }
  }, [open, openedOnce]);

  const iframeURLParams = new URLSearchParams({
    stream,
    assistantType,
    parentURL: encodeURIComponent(window.location.href),
    niceName: assistantConfig?.niceName || assistantType,
    width,
  });
  URLSearchParams;
  const iframeURL = `${baseURL}/${uiType}?${iframeURLParams.toString()}`;
  clog('DIGORIFrame render:', { open, openedOnce, iframeURL });

  return (
    <Box
      direction="vertical"
      className="DIGORIFrameWrapper"
      display={open ? 'flex' : 'none'}
      height="100%"
      width="100%"
      align="center"
      verticalAlign="middle"
    >
      <TopBar
        onClose={onClose}
        assistantType={assistantType}
        uiType={uiType}
        sendMessageToIframe={sendMessageToIframe}
        stream={stream}
        fullScreen={fullScreen}
        toggleFullScreen={toggleFullScreen}
      />
      {!ready ? (
        <Box
          direction="vertical"
          align="center"
          verticalAlign="middle"
          minWidth="100%"
          height="100%"
        >
          <Loader size="tiny" text="Loading..." />
        </Box>
      ) : null}

      <IframeComponent
        openedOnce={openedOnce}
        assistantType={assistantType}
        uiType={uiType}
        stream={stream}
        iframeRef={iframeRef}
        iframeURLParams={iframeURLParams}
      />
    </Box>
  );
};

export const DIGORIFrame = React.memo(DIGORIFrame_, (prev, next) => {
  return (
    prev.open === next.open &&
    prev.assistantType === next.assistantType &&
    prev.fullScreen === next.fullScreen
  );
});
